import moment from 'moment'
import { removerTimezoneOffSet } from '../../../utils'

type Ponto = {
  horario: string,
  origem: 'REPP' | 'OUTRO'
}

/**
 * Obtém os pontos do dia a partir dos pontos registrados.
 *
 * @param {Ponto[]} listaDePontos - Array de objetos Ponto contendo os registros de pontos.
 * @returns {Object[]} - Retorna um array contendo os pontos do dia formatados para exibição.
 */
export function pegarListaDePontosDoDia (listaDePontos: Ponto[]): Object[] {
  if (!listaDePontos) return []
  const pontosDoDia = listaDePontos.slice(0, 5)
  return pontosDoDia.map(formatarPonto)
}

/**
 * Formata um ponto para exibição no formato dd/mm/aa HH:MM:SS*, sendo que o asterisco será adicionado se for um ponto do REP-P
 *
 * @param {Ponto} ponto - Objeto Ponto a ser formatado.
 * @returns {Object} - Retorna um objeto contendo o ponto formatado para exibição.
 */
function formatarPonto (ponto: Ponto): Object {
  const ehPontoDoREPP = ponto.origem === 'REPP'
  const origemDoPonto = ehPontoDoREPP ? '' : '*'

  const date = ehPontoDoREPP ? formatarHorarioPontoREPP(ponto.horario) : formatarHorario(ponto.horario)
  const diaDoPonto = formatarDia(date as unknown as string)
  const horaDoPonto = formatarHora(date as unknown as string)
  const dataFormatada = formatarPontoParaBotao(diaDoPonto, horaDoPonto, origemDoPonto)

  return dataFormatada
}

/**
 * Formata o horário de um ponto REPP.
 *
 * @param {string} horario - Horário do ponto a ser formatado.
 * @returns {Object} - Retorna o horário do ponto REPP formatado.
 */
function formatarHorarioPontoREPP (horario: string): Object {
  const horarioSemTimezone = removerTimezoneOffSet(horario)
  return moment.utc(horarioSemTimezone)
}

/**
 * Formata o horário de um ponto.
 *
 * @param {string} horario - Horário do ponto a ser formatado.
 * @returns {Object} - Retorna o horário do ponto formatado.
 */
function formatarHorario (horario: string): Object {
  return moment(horario)
}

/**
 * Formata a data de um ponto.
 *
 * @param {string} data - Data a ser formatada.
 * @returns {string} - Retorna a data do ponto formatada.
 */
function formatarDia (data: string): string {
  return String(moment(data).format('DD/MM/YY'))
}

/**
 * Formata a hora de um ponto.
 *
 * @param {string} date - Data a ser formatada.
 * @returns {string} - Retorna a hora do ponto formatada.
 */
function formatarHora (date: string): string {
  return String(moment(date).format('HH:mm:ss'))
}

/**
 * Formata um ponto para exibição em um botão.
 *
 * @param {string} diaDoPonto - Dia do ponto formatado.
 * @param {string} horaDoPonto - Hora do ponto formatado.
 * @param {string} origemDoPonto - Origem do ponto formatado.
 * @returns {Object} - Retorna o ponto formatado para exibição em um botão.
 */
function formatarPontoParaBotao (diaDoPonto: string, horaDoPonto: string, origemDoPonto: string = ''): Object {
  return {
    dia: diaDoPonto,
    hora: horaDoPonto,
    origem: origemDoPonto
  }
}
